// src/pages/merchant/Settings.js
// Paramètres marchand : profil, logo/couverture (société), mot de passe
// Maxula

import React, { useEffect, useState, useRef } from 'react';
import { FiUpload, FiSave, FiLock, FiEye } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { merchantAPI, userAPI, categoryAPI } from '../../services/api';
import { normalizeImagePath } from '../../utils/imageUtils';
import ServiceLocationsCard from '../../components/merchant/ServiceLocationsCard';

const MerchantSettings = () => {
  const [form, setForm] = useState({
    businessName: '', legalName: '', description: '', category: '', phone: '', email: '', businessType: 'merchant',
    ice: '', ifNumber: '', rc: '', legalAddress: '', tvaRegistered: false, tvaRate: 0
  });
  const [bank, setBank] = useState({ accountHolder: '', bankName: '', rib: '', iban: '', swift: '' });
  const [showSecrets, setShowSecrets] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoryChoice, setCategoryChoice] = useState(''); // '' | nom de catégorie | '__other__'
  const [logoFile, setLogoFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [legalVerified, setLegalVerified] = useState(false);
  const [storeRef, setStoreRef] = useState(null);
  const [signatureFile, setSignatureFile] = useState(null);
  const [signaturePreview, setSignaturePreview] = useState(null);
  const [publicCode, setPublicCode] = useState('');

  const [pwd, setPwd] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [savingPwd, setSavingPwd] = useState(false);

  const logoRef = useRef();
  const coverRef = useRef();

  useEffect(() => {
    (async () => {
      try {
        let catNames = [];
        try {
          const cr = await categoryAPI.getAll();
          const arr = cr.data?.data || cr.data || [];
          catNames = (Array.isArray(arr) ? arr : []).map((c) => c.name).filter(Boolean);
          setCategories(catNames);
        } catch (e) { /* catégories optionnelles */ }

        const res = await merchantAPI.getProfile();
        const m = res.data?.data || res.data?.merchant || {};
        setForm({
          businessName: m.businessName || '',
          legalName: m.legalName || '',
          description: m.description || '',
          category: m.category || '',
          phone: m.phone || '',
          email: m.email || '',
          businessType: m.businessType || 'merchant',
          ice: m.ice || '',
          ifNumber: m.ifNumber || '',
          rc: m.rc || '',
          legalAddress: m.legalAddress || '',
          tvaRegistered: !!m.tvaRegistered,
          tvaRate: m.tvaRate != null ? m.tvaRate : 0
        });
        setLegalVerified(!!m.legalNameVerified);
        setStoreRef(m.slug || m.id || m._id || null);
        setPublicCode(m.publicCode || '');
        if (m.signatureUrl) setSignaturePreview(normalizeImagePath(m.signatureUrl));
        const cat = m.category || '';
        // Le champ "Autre" n'apparait QUE si l'utilisateur choisit explicitement
        // "Autre…" dans la liste. On n'ouvre pas le champ automatiquement au
        // chargement, meme si la catégorie enregistrée n'est pas dans la liste
        // (la valeur reste conservée dans le formulaire et n'est pas perdue).
        if (cat && catNames.includes(cat)) setCategoryChoice(cat);
        else setCategoryChoice('');
        if (m.logo) setLogoPreview(normalizeImagePath(m.logo));
        if (m.coverImage) setCoverPreview(normalizeImagePath(m.coverImage));
        if (m.bankInfo) setBank({
          accountHolder: m.bankInfo.accountHolder || '',
          bankName: m.bankInfo.bankName || '',
          rib: m.bankInfo.rib || '',
          iban: m.bankInfo.iban || '',
          swift: m.bankInfo.swift || ''
        });
      } catch (e) {
        toast.error('Erreur de chargement du profil');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const onField = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const pickLogo = (e) => {
    const f = e.target.files?.[0];
    if (f) { setLogoFile(f); setLogoPreview(URL.createObjectURL(f)); }
  };
  const pickCover = (e) => {
    const f = e.target.files?.[0];
    if (f) { setCoverFile(f); setCoverPreview(URL.createObjectURL(f)); }
  };

  const [savingMedia, setSavingMedia] = useState(false);
  const saveMedia = async () => {
    if (!logoFile && !coverFile) return toast.error('Choisissez un logo ou une couverture');
    try {
      setSavingMedia(true);
      const fd = new FormData();
      if (logoFile) fd.append('logo', logoFile);
      if (coverFile) fd.append('coverImage', coverFile);
      const res = await merchantAPI.updateMedia(fd);
      const m = res.data?.data;
      if (m?.logo) setLogoPreview(normalizeImagePath(m.logo));
      if (m?.coverImage) setCoverPreview(normalizeImagePath(m.coverImage));
      setLogoFile(null); setCoverFile(null);
      toast.success('Photos enregistrées');
    } catch (e) { toast.error('Erreur lors de l\'envoi des photos'); }
    finally { setSavingMedia(false); }
  };

  const savePaymentAccount = async () => {
    try {
      setSaving(true);
      await merchantAPI.updatePaymentAccount({ paymentAccount: { method: 'none', enabled: false }, bankInfo: bank });
      toast.success('RIB enregistré');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Erreur lors de l\'enregistrement');
    } finally { setSaving(false); }
  };

  const saveProfile = async () => {
    if (!String(form.legalName || '').trim()) {
      toast.error('Le nom légal / raison sociale est obligatoire.');
      return;
    }
    try {
      setSaving(true);
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (k === 'logo' || k === 'coverImage') return; // gérés via fichiers uniquement
        fd.append(k, v);
      });
      if (logoFile) fd.append('logo', logoFile);
      if (coverFile) fd.append('coverImage', coverFile);
      if (signatureFile) fd.append('signature', signatureFile);
      fd.append('bankInfo', JSON.stringify(bank));
      fd.append('paymentAccount', JSON.stringify({ method: 'none', enabled: false }));
      const res = await merchantAPI.updateProfile(fd);
      const m = res.data?.data;
      if (m?.logo) setLogoPreview(normalizeImagePath(m.logo));
      if (m?.coverImage) setCoverPreview(normalizeImagePath(m.coverImage));
      if (m?.signatureUrl) setSignaturePreview(normalizeImagePath(m.signatureUrl));
      setLogoFile(null); setCoverFile(null); setSignatureFile(null);
      toast.success('Profil mis à jour');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const savePassword = async () => {
    if (pwd.newPassword.length < 8) return toast.error('8 caractères minimum');
    if (pwd.newPassword !== pwd.confirm) return toast.error('Les mots de passe ne correspondent pas');
    try {
      setSavingPwd(true);
      await userAPI.updatePassword({ currentPassword: pwd.currentPassword, newPassword: pwd.newPassword });
      toast.success('Mot de passe modifié');
      setPwd({ currentPassword: '', newPassword: '', confirm: '' });
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Mot de passe actuel incorrect');
    } finally {
      setSavingPwd(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-24"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" /></div>;
  }

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">Paramètres</h1>

      {/* Médias */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
        <h2 className="font-semibold text-gray-900 mb-4">Logo & couverture</h2>
        <div className="relative h-32 rounded-xl bg-gray-100 overflow-hidden mb-3">
          {coverPreview && <img src={coverPreview} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} className="w-full h-full object-cover" />}
          <button onClick={() => coverRef.current?.click()}
            className="absolute bottom-2 right-2 bg-white/90 px-3 py-1.5 rounded-lg text-sm flex items-center gap-1 hover:bg-white">
            <FiUpload size={14} /> Couverture
          </button>
          <input ref={coverRef} type="file" accept="image/*" onChange={pickCover} className="hidden" />
        </div>
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 rounded-xl bg-gray-100 overflow-hidden flex items-center justify-center">
            {logoPreview ? <img src={logoPreview} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} className="w-full h-full object-cover" />
              : <span className="text-2xl text-gray-300">?</span>}
          </div>
          <button onClick={() => logoRef.current?.click()}
            className="px-3 py-2 rounded-lg border border-gray-300 text-sm flex items-center gap-1 hover:bg-gray-50">
            <FiUpload size={14} /> Changer le logo
          </button>
          <input ref={logoRef} type="file" accept="image/*" onChange={pickLogo} className="hidden" />
          {storeRef && (
            <a
              href={`/merchants/${storeRef}`}
              target="_blank"
              rel="noopener noreferrer"
              title="Voir ma boutique publique (état enregistré)"
              className="px-3 py-2 rounded-lg border border-gray-300 text-sm flex items-center gap-1 hover:bg-gray-50 text-gray-700"
            >
              <FiEye size={14} /> Aperçu
            </a>
          )}
          <button onClick={saveMedia} disabled={savingMedia}
            className="ml-auto px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 disabled:opacity-60">
            {savingMedia ? 'Envoi…' : 'Enregistrer les photos'}
          </button>
        </div>
      </div>

      {/* Lieux de prestation — adresse + horaires saisis une seule fois */}
      <ServiceLocationsCard />

      {/* Infos */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
        <h2 className="font-semibold text-gray-900 mb-4">Informations</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium mb-1">Nom de l'entreprise</label>
            <input name="businessName" value={form.businessName} onChange={onField} className="w-full border rounded-lg px-3 py-2" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium mb-1">Nom légal / Raison sociale <span className="text-red-500">*</span></label>
            {legalVerified ? (
              <>
                <input value={form.legalName} disabled className="w-full border rounded-lg px-3 py-2 bg-gray-100 text-gray-600" />
                <p className="text-xs text-gray-500 mt-1">Nom légal vérifié — pour le modifier, contactez le support.</p>
              </>
            ) : (
              <>
                <input name="legalName" value={form.legalName} onChange={onField} className="w-full border rounded-lg px-3 py-2" placeholder="Selon vos papiers (CIN / RC / ICE)" />
                <p className="text-xs text-gray-500 mt-1">Raison sociale (personne morale) ou nom complet (personne physique). Modifiable jusqu'à validation par Maxula.</p>
              </>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Type</label>
            <select name="businessType" value={form.businessType} onChange={onField} className="w-full border rounded-lg px-3 py-2">
              <option value="merchant">Marchand</option>
              <option value="promoteur">Promoteur</option>
              <option value="agence">Agence</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Catégorie</label>
            <select
              value={categoryChoice}
              onChange={(e) => {
                const v = e.target.value;
                setCategoryChoice(v);
                if (v === '__other__') {
                  if (categories.includes(form.category)) setForm((f) => ({ ...f, category: '' }));
                } else {
                  setForm((f) => ({ ...f, category: v }));
                }
              }}
              className="w-full border rounded-lg px-3 py-2"
            >
              <option value="">— Choisir —</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              <option value="__other__">Autre…</option>
            </select>
            {categoryChoice === '__other__' && (
              <input name="category" value={form.category} onChange={onField} placeholder="Précisez la catégorie"
                className="w-full border rounded-lg px-3 py-2 mt-2" />
            )}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Téléphone</label>
            <input name="phone" value={form.phone} onChange={onField} className="w-full border rounded-lg px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Email</label>
            <input name="email" value={form.email} onChange={onField} className="w-full border rounded-lg px-3 py-2" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium mb-1">À propos</label>
            <textarea name="description" value={form.description} onChange={onField} rows={3} placeholder="Présentez votre entreprise en quelques lignes" className="w-full border rounded-lg px-3 py-2" />
          </div>
        </div>
        <button onClick={saveProfile} disabled={saving}
          className="mt-4 px-5 py-2.5 rounded-lg bg-primary-600 text-white font-semibold hover:bg-primary-700 flex items-center gap-2 disabled:opacity-60">
          <FiSave /> {saving ? 'Enregistrement…' : 'Enregistrer'}
        </button>
      </div>

      {/* Identité légale & facturation (Brique 1) */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
        <h2 className="font-semibold text-gray-900 mb-1">Identité légale & facturation</h2>
        <p className="text-sm text-gray-500 mb-4">
          Ces informations apparaissent sur les factures émises par Maxula <b>pour votre compte</b> (mandat de facturation).
          Vous restez le vendeur légal. La TVA appliquée dépend de votre statut déclaré ci-dessous.
        </p>

        {publicCode && (
          <div className="mb-4 inline-flex items-center gap-2 rounded-lg bg-gray-50 border border-gray-200 px-3 py-2">
            <span className="text-xs text-gray-500">Identifiant marchand public</span>
            <span className="font-mono font-semibold text-gray-800">{publicCode}</span>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">ICE</label>
            <input name="ice" value={form.ice} onChange={onField} className="w-full border rounded-lg px-3 py-2" placeholder="Identifiant Commun de l'Entreprise" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">IF (Identifiant Fiscal)</label>
            <input name="ifNumber" value={form.ifNumber} onChange={onField} className="w-full border rounded-lg px-3 py-2" placeholder="N° d'identifiant fiscal" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">RC (Registre de Commerce)</label>
            <input name="rc" value={form.rc} onChange={onField} className="w-full border rounded-lg px-3 py-2" placeholder="N° RC" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Adresse légale</label>
            <input name="legalAddress" value={form.legalAddress} onChange={onField} className="w-full border rounded-lg px-3 py-2" placeholder="Adresse de l'entreprise" />
          </div>

          <div className="md:col-span-2 border-t border-gray-100 pt-4">
            <label className="inline-flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.tvaRegistered}
                onChange={(e) => setForm((f) => ({ ...f, tvaRegistered: e.target.checked, tvaRate: e.target.checked ? (f.tvaRate || 20) : 0 }))}
                className="h-4 w-4 accent-primary-600"
              />
              <span className="text-sm font-medium text-gray-800">Je suis assujetti à la TVA</span>
            </label>
            <p className="text-xs text-gray-500 mt-1">
              Si vous n'êtes pas assujetti (cas de la plupart des petits commerces), laissez décoché : vos factures indiqueront « TVA non applicable ».
            </p>
          </div>

          {form.tvaRegistered && (
            <div>
              <label className="block text-sm font-medium mb-1">Taux de TVA (%)</label>
              <input type="number" name="tvaRate" value={form.tvaRate} onChange={onField} min="0" step="1" className="w-full border rounded-lg px-3 py-2" placeholder="20" />
            </div>
          )}

          <div className="md:col-span-2">
            <label className="block text-sm font-medium mb-1">Signature / cachet (image)</label>
            <div className="flex items-center gap-4">
              {signaturePreview && (
                <img src={signaturePreview} alt="Signature" className="h-16 border border-gray-200 rounded bg-white object-contain" />
              )}
              <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 cursor-pointer hover:bg-gray-50 text-sm">
                <FiUpload />
                <span>{signatureFile ? signatureFile.name : 'Choisir une image'}</span>
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files && e.target.files[0];
                    if (file) { setSignatureFile(file); setSignaturePreview(URL.createObjectURL(file)); }
                  }}
                />
              </label>
            </div>
            <p className="text-xs text-gray-500 mt-1">Apparaîtra sur vos factures légales. Optionnel mais recommandé.</p>
          </div>
        </div>

        <button onClick={saveProfile} disabled={saving}
          className="mt-4 px-5 py-2.5 rounded-lg bg-primary-600 text-white font-semibold hover:bg-primary-700 flex items-center gap-2 disabled:opacity-60">
          <FiSave /> {saving ? 'Enregistrement…' : 'Enregistrer'}
        </button>
      </div>

      {/* RIB pour mes retraits */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
        <h2 className="font-semibold text-gray-900 mb-1">RIB pour mes retraits</h2>
        <p className="text-sm text-gray-500 mb-3">Ce RIB/IBAN sert à recevoir vos retraits. Renseignez-le pour pouvoir demander un retrait depuis votre portefeuille.</p>
        <label className="inline-flex items-center gap-2 text-xs text-gray-500 mb-4 cursor-pointer">
          <input type="checkbox" checked={showSecrets} onChange={(e) => setShowSecrets(e.target.checked)} />
          Afficher mes informations sensibles
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Titulaire du compte</label>
            <input value={bank.accountHolder} onChange={(e) => setBank((b) => ({ ...b, accountHolder: e.target.value }))} className="w-full border rounded-lg px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Banque</label>
            <input value={bank.bankName} onChange={(e) => setBank((b) => ({ ...b, bankName: e.target.value }))} className="w-full border rounded-lg px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">RIB</label>
            <input type={showSecrets ? "text" : "password"} autoComplete="off" value={bank.rib} onChange={(e) => setBank((b) => ({ ...b, rib: e.target.value }))} className="w-full border rounded-lg px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">IBAN</label>
            <input type={showSecrets ? "text" : "password"} autoComplete="off" value={bank.iban} onChange={(e) => setBank((b) => ({ ...b, iban: e.target.value }))} className="w-full border rounded-lg px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">SWIFT/BIC</label>
            <input type={showSecrets ? "text" : "password"} autoComplete="off" value={bank.swift} onChange={(e) => setBank((b) => ({ ...b, swift: e.target.value }))} className="w-full border rounded-lg px-3 py-2" />
          </div>
        </div>
        <button onClick={savePaymentAccount} disabled={saving}
          className="mt-4 px-5 py-2.5 rounded-lg bg-primary-600 text-white font-semibold hover:bg-primary-700 flex items-center gap-2 disabled:opacity-60">
          <FiSave /> Enregistrer
        </button>
      </div>

      {/* Mot de passe */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2"><FiLock /> Mot de passe</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <input type="password" placeholder="Mot de passe actuel" value={pwd.currentPassword}
            onChange={(e) => setPwd((p) => ({ ...p, currentPassword: e.target.value }))} className="border rounded-lg px-3 py-2" />
          <input type="password" placeholder="Nouveau (min. 8)" value={pwd.newPassword}
            onChange={(e) => setPwd((p) => ({ ...p, newPassword: e.target.value }))} className="border rounded-lg px-3 py-2" />
          <input type="password" placeholder="Confirmer" value={pwd.confirm}
            onChange={(e) => setPwd((p) => ({ ...p, confirm: e.target.value }))} className="border rounded-lg px-3 py-2" />
        </div>
        <button onClick={savePassword} disabled={savingPwd}
          className="mt-4 px-5 py-2.5 rounded-lg border border-gray-300 font-semibold hover:bg-gray-50 disabled:opacity-60">
          {savingPwd ? 'Modification…' : 'Modifier le mot de passe'}
        </button>
      </div>
    </div>
  );
};

export default MerchantSettings;
